import { useCallback, useEffect, useState } from "react";

import {
  AdminLocationResponseItem,
  AdminLocationRow,
  GetAdminLocationsResponse,
  LocationCategory,
} from "@/components/export-locations-admin/types";
import { useNotifications } from "@/contexts/NotificationsContext";

import { useApi } from "../useApi";

/**
 * Data access for the admin "Export Locations" tab.
 *
 * Reads ALL export locations (enabled + disabled) from the admin-gated
 * ``GET /admin/export-locations`` so an admin can see and enable disabled
 * ones, and persists the ``enabled`` flag via
 * ``PATCH /admin/export-locations/{id}`` (optimistic, rolled back on failure).
 * The Transfer tab uses ``useAdminTransferLocations``.
 */

/** Display category derived from the location name (UI grouping only). */
const deriveCategory = (name: string): LocationCategory => {
  const n = name.toLowerCase();
  if (n.includes("evs") || n.includes("scan") || n.includes("control room")) {
    return "EVS";
  }
  if (n.includes("isg") || n.includes("truck")) return "ISG";
  if (n.includes("archive")) return "Archive";
  if (n.includes("cloud") || n.includes("dlp") || n.includes("staging")) {
    return "Cloud";
  }
  return "Other";
};

const toRow = (loc: AdminLocationResponseItem): AdminLocationRow => ({
  id: loc.id,
  displayName: loc.displayName,
  category: deriveCategory(loc.displayName),
  enabled: loc.enabled,
  lastChangedBy: loc.lastChangedBy,
  lastChangedAt: loc.lastChangedAt,
});

const useAdminLocations = () => {
  const [rows, setRows] = useState<AdminLocationRow[]>([]);
  const [loading, setLoading] = useState(false);

  const { notifyError } = useNotifications();
  // isAdmin: true sends the access token the admin authorizer validates.
  const { apiGet, apiPatch } = useApi({ isAdmin: true });

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiGet<GetAdminLocationsResponse>(
        "/admin/export-locations"
      );
      setRows((response?.locations ?? []).map(toRow));
    } catch (error) {
      console.error(error);
      notifyError("Locations Error", "Failed to load export locations");
    } finally {
      setLoading(false);
    }
  }, [apiGet, notifyError]);

  /** Persist a location's enabled flag (optimistic; rolls back on failure). */
  const setEnabled = useCallback(
    async (id: string, enabled: boolean) => {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, enabled } : r))
      );
      try {
        const response = await apiPatch<{
          location: Partial<AdminLocationResponseItem>;
        }>(`/admin/export-locations/${id}`, { enabled });
        // The server derives "who made this change" from the authorizer
        // context -- adopt the stamp from the response rather than guess it
        // optimistically, since the client doesn't know its own identity.
        const patched = response?.location;
        if (patched) {
          setRows((prev) =>
            prev.map((r) =>
              r.id === id
                ? {
                    ...r,
                    lastChangedBy: patched.lastChangedBy ?? r.lastChangedBy,
                    lastChangedAt: patched.lastChangedAt ?? r.lastChangedAt,
                  }
                : r
            )
          );
        }
      } catch (error) {
        console.error(error);
        setRows((prev) =>
          prev.map((r) => (r.id === id ? { ...r, enabled: !enabled } : r))
        );
        notifyError(
          "Locations Error",
          `Failed to ${enabled ? "enable" : "disable"} the export location`
        );
      }
    },
    [apiPatch, notifyError]
  );

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  return { rows, loading, loadLocations, setEnabled };
};

export default useAdminLocations;
